import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LazyImage from '../components/LazyImage';
import NewsFeed from '../components/NewsFeed';
import apiService from '../services/apiService';
import {
  HomeContainer,
  HeroSection,
  HeroContent,
  HeroTextContent,
  HeroImageContent,
  HeroTitle,
  HeroSubtitle,
  HeroButton,
  AboutPreviewSection,
  Container,
  SectionTitle,
  SectionSubtitle,
  AboutPreviewContent,
  AboutText,
  AboutImage,
  ServicesPreviewSection,
  ServicesGrid,
  ServiceCard,
  ServiceIcon,
  ServiceTitle,
  ServiceDescription,
  CTASection,
  CTATitle,
  CTAText,
  CTAButton
} from '../styles/HomeStyles';

const Home = () => {
  const navigate = useNavigate();
  const [heroContent, setHeroContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [servicesContent, setServicesContent] = useState([]);


  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [hero, about, services] = await Promise.all([
          apiService.getHeroContent(),
          apiService.getAboutContent(),
          apiService.getServicesContent()
        ]);
        
        setHeroContent(hero);
        setAboutContent(about);
        setServicesContent(services.slice(0, 3)); // Show only first 3 services
      } catch (error) {
        console.error('Error fetching content:', error);
        // Set default content if API fails
        setHeroContent({
          heading: 'Welcome to Cocoa-Pro Corporation',
          subheading: 'Leading cocoa processing and agricultural services in Liberia'
        });
        setAboutContent({
          bio: 'Cocoa-Pro Corporation is a leading agricultural company in Liberia, specializing in cocoa processing and agricultural consulting services.',
          mission: 'To provide exceptional cocoa processing services while supporting local farmers and communities.',
          vision: 'To be the premier cocoa processing company in West Africa.'
        });
        setServicesContent([
          {
            name: 'Cocoa Processing',
            description: 'Professional cocoa bean processing with state-of-the-art equipment and quality control.'
          },
          {
            name: 'Agricultural Consulting',
            description: 'Expert consulting services for farmers and agricultural businesses.'
          },
          {
            name: 'Quality Control',
            description: 'Comprehensive quality assurance and testing services for agricultural products.'
          }
        ]);
      }
    };

    fetchContent();
  }, []);

  const handleLearnMore = () => {
    navigate('/about');
  };

  const handleContactUs = () => {
    navigate('/contact');
  };



  return (
    <HomeContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTextContent>
            <HeroTitle>
              {heroContent?.heading || 'Welcome to Cocoa-Pro Corporation'}
            </HeroTitle>
            <HeroSubtitle>
              {heroContent?.subheading || 'Leading cocoa processing and agricultural services in Liberia'}
            </HeroSubtitle>
            <HeroButton onClick={handleLearnMore}>
              Learn More About Us
            </HeroButton>
          </HeroTextContent>
          
          <HeroImageContent>
            <LazyImage
              src={heroContent?.imageUrl}
              alt="Cocoa-Pro Corporation"
              loadingHeight="400px"
            />
          </HeroImageContent>
        </HeroContent>
      </HeroSection>

      {/* News Feed Section */}
      <NewsFeed limit={3} />

      {/* About Preview Section */}
      <AboutPreviewSection>
        <Container>
          <SectionTitle>About Cocoa-Pro Corporation</SectionTitle>
          <SectionSubtitle>
            Committed to excellence in cocoa processing and agricultural services
          </SectionSubtitle>
          
          <AboutPreviewContent>
            <AboutText>
              <h3>Our Story</h3>
              <p>
                {aboutContent?.bio || 'Cocoa-Pro Corporation is a leading agricultural company in Liberia, specializing in cocoa processing and agricultural consulting services.'}
              </p>
              <p>
                <strong>Mission:</strong> {aboutContent?.mission || 'To provide exceptional cocoa processing services while supporting local farmers and communities.'}
              </p>
              <p>
                <strong>Vision:</strong> {aboutContent?.vision || 'To be the premier cocoa processing company in West Africa.'}
              </p>
            </AboutText>
            
            <AboutImage>
              <LazyImage
                src={aboutContent?.imageUrl}
                alt="Cocoa-Pro Corporation"
                loadingHeight="400px"
              />
            </AboutImage>
          </AboutPreviewContent>
        </Container>
      </AboutPreviewSection>

      {/* Services Preview Section */}
      <ServicesPreviewSection>
        <Container>
          <SectionTitle>Our Services</SectionTitle>
          <SectionSubtitle>
            Comprehensive solutions for all your cocoa and agricultural needs
          </SectionSubtitle>
          
          <ServicesGrid>
            {servicesContent.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceIcon>
                  {index === 0 && '🌱'}
                  {index === 1 && '🔬'}
                  {index === 2 && '✅'}
                  {index > 2 && '🏭'}
                </ServiceIcon>
                <ServiceTitle>{service.name}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ServicesPreviewSection>

      {/* Call to Action Section */}
      <CTASection>
        <Container>
          <CTATitle>Ready to Work With Us?</CTATitle>
          <CTAText>
            Contact us today to learn more about our services and how we can help 
            your agricultural business thrive.
          </CTAText>
          <CTAButton onClick={handleContactUs}>
            Get In Touch
          </CTAButton>
        </Container>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;
